import { create } from 'zustand';
import ThemeService from '../services/ThemeService';
import DataService from '../services/DataService';
import useAppStore from './appStore';

// NAV-1-DENSITY-FIRST-RUN-1: density is now two-axis (archetype × mode).
// `density` (mode) drives the data-density DOM attribute and is the value
// the SETTINGS-HUB-1a SettingsToggle reads/writes. `archetype` rides
// alongside in store state and persists through the canonical
// user_preferences path; we do not apply it as a DOM attribute in this
// seat (§9 — no visual design changes).
const VALID_MODES      = ['compact', 'comfortable', 'spacious'];
const VALID_ARCHETYPES = ['bloomberg', 'hybrid', 'linear'];

function getInitialState() {
  ThemeService.init();
  return {
    palette: ThemeService.currentPalette(), // 'heritage' | 'platform' | 'modern'
    mode:    ThemeService.currentMode(),    // 'light' | 'dark'
  };
}

// Defaults before Firestore hydration. PreferencesContext calls
// hydrateDensityPreference / setDensityPreference on auth to overwrite.
function getInitialDensity()   { return 'comfortable'; }
function getInitialArchetype() { return 'hybrid'; }

function applyDensity(value) {
  document.documentElement.setAttribute('data-density', value);
  document.body.setAttribute('data-density', value);
}

function cacheArchetype(_value) {
  // no-op: archetype is persisted to Firestore via DataService.setUserDensityPreference
}

/**
 * Persist density preference via the canonical user_preferences path
 * (PreferencesService) and emit a density.toggle telemetry event.
 *
 * Callers in this file:
 *   - setDensity(mode)            → toggle (currentArchetype preserved)
 *   - setDensityPreference(...)   → seed/restore (both axes set together)
 *
 * Best-effort: a Firestore failure does not roll back the local UI change;
 * localStorage and DOM attribute already reflect the value, and the next
 * authenticated session will reseed via PreferencesContext.
 */
async function persistAndInstrument({
  tenantId, uid, oldArchetype, oldMode, newArchetype, newMode, isFirstRunSeed, emitTelemetry,
}) {
  if (!tenantId || !uid) return;
  try {
    await DataService.setUserDensityPreference(tenantId, uid, newArchetype, newMode, { isFirstRunSeed });
  } catch {
    // Non-fatal — localStorage already has the value.
  }
  if (emitTelemetry) {
    try {
      await DataService.emitDensityTelemetry({
        tenantId,
        userId: uid,
        fromArchetype: oldArchetype ?? null,
        fromMode:      oldMode ?? null,
        toArchetype:   newArchetype,
        toMode:        newMode,
      });
    } catch { /* telemetry is best-effort */ }
  }
}

applyDensity(getInitialDensity());
cacheArchetype(getInitialArchetype());

const { palette: initPalette, mode: initMode } = getInitialState();

const useThemeStore = create((set, get) => ({
  palette: initPalette,
  mode:    initMode,
  // Legacy alias — components that read `theme` get the mode string
  theme:   initMode,
  density:   getInitialDensity(),
  archetype: getInitialArchetype(),

  setPalette: (palette) => set(() => {
    const next = ThemeService.setPalette(palette);
    return { palette: next };
  }),

  setMode: (mode) => set(() => {
    const next = ThemeService.setMode(mode);
    return { mode: next, theme: next };
  }),

  toggleMode: () => set(() => {
    const next = ThemeService.toggleMode();
    return { mode: next, theme: next };
  }),

  // Legacy aliases — PreferencesContext and TopBar use these
  toggleTheme: () => set(() => {
    const next = ThemeService.toggleMode();
    return { mode: next, theme: next };
  }),
  setTheme: (value) => set(() => {
    const next = ThemeService.setMode(value);
    return { mode: next, theme: next };
  }),

  /**
   * User-initiated density toggle (mode only). Existing toggle UIs
   * (DensityToggle.jsx, TopBar.jsx) cycle through modes without changing
   * archetype — by design per dd-decision-006 (manifest informational
   * post-first-run, archetype is not user-tunable in this seat).
   *
   * Persists to canonical user_preferences path and emits a
   * density.toggle telemetry event for EXEC-49 §6.a monitoring.
   */
  setDensity: (mode) => {
    if (!VALID_MODES.includes(mode)) return;
    const prev = get();
    applyDensity(mode);
    set({ density: mode });
    const { tenantId, currentUser } = useAppStore.getState();
    persistAndInstrument({
      tenantId,
      uid: currentUser?.uid,
      oldArchetype: prev.archetype,
      oldMode:      prev.density,
      newArchetype: prev.archetype,
      newMode:      mode,
      isFirstRunSeed: false,
      emitTelemetry: true,
    });
  },

  /**
   * Set both density axes together. Called by PreferencesContext on
   * first-run seed (from primary-module manifest) and on legacy backfill
   * (from getUserDensityPreference's migration path).
   *
   * `opts.isFirstRunSeed` distinguishes the cases for downstream telemetry.
   * `opts.emitTelemetry` defaults to false — first-run seeding is not a
   * user-initiated toggle and must not pollute EXEC-49's toggle-rate
   * baseline.
   */
  setDensityPreference: (archetype, mode, opts = {}) => {
    if (!VALID_ARCHETYPES.includes(archetype) || !VALID_MODES.includes(mode)) return;
    const prev = get();
    applyDensity(mode);
    cacheArchetype(archetype);
    set({ density: mode, archetype });
    const { tenantId, currentUser } = useAppStore.getState();
    persistAndInstrument({
      tenantId,
      uid: currentUser?.uid,
      oldArchetype: prev.archetype,
      oldMode:      prev.density,
      newArchetype: archetype,
      newMode:      mode,
      isFirstRunSeed: !!opts.isFirstRunSeed,
      emitTelemetry: !!opts.emitTelemetry,
    });
  },

  /**
   * Adopt a preference loaded from Firestore without re-persisting it.
   * Used by PreferencesContext's seed effect when an existing preference
   * is found (the existing-pref branch of the soft-default contract):
   * we update local state + DOM but skip the round-trip write.
   */
  hydrateDensityPreference: (archetype, mode) => {
    if (!VALID_MODES.includes(mode)) return;
    if (VALID_ARCHETYPES.includes(archetype)) {
      cacheArchetype(archetype);
      set({ archetype });
    }
    applyDensity(mode);
    set({ density: mode });
  },
}));

export default useThemeStore;
